import React, { useContext, useState } from 'react';
import styled from 'styled-components';
import { pick } from 'lodash';

import { Button } from 'src/presentation/components/agent_desktop_components';
import { Table, TBody, TH, THead, TR } from '@ad-scripts/presentation/components/common/table';
import { StyledTH } from 'src/presentation/components/pages/shared/Table';
import FormContext from 'src/presentation/contexts/FormContext';
import generateId from 'src/lib/generateId';

import {
  NameValueList,
  TaggedNameValueList,
  TaggedNameValuePair,
} from 'src/presentation/components/pages/integrations/types';
import NameValueListRow from 'src/presentation/components/pages/integrations/editorComponents/NameValueListRow';

/**
 * "Editor" component for the Custom Headers list.
 */

export type NameValueListEditorProps = {
  addButtonLabel: string;
  dataAid: string;
  items: NameValueList;
  onChange: (newValue: NameValueList) => void;
  nameColumnLabel: string;
  valueColumnLabel: string;
  maxItemCount?: number;
  maskedItemNames?: string[]; // List of "secret" item names that need to have the value masked
};

const NameValueListEditor = (props: NameValueListEditorProps): JSX.Element => {
  const TAG_NEW = 'new';
  const { errors, resetError } = useContext(FormContext);
  const [currentState, setCurrentState] = useState<TaggedNameValueList>(buildState());

  function buildState(): TaggedNameValueList {
    return props.items.length ? props.items : [{ id: generateId(), name: '', tag: TAG_NEW }];
  }

  function stripTags(items: TaggedNameValueList): NameValueList {
    return items.map(item => pick(item, ['id', 'name', 'value']));
  }

  function handleRowChange(item: TaggedNameValuePair): void {
    const state = [...currentState];
    const index = state.findIndex(elt => elt.id === item.id);
    if (index >= 0) {
      state[index] = item;
      setCurrentState(state);
      props.onChange(stripTags(state));
    }
  }

  function handleRowDelete(row: TaggedNameValuePair): void {
    if (row.isRequired) return;

    const state = [...currentState];
    const index = state.findIndex(item => item.id === row.id);
    if (index >= 0) {
      state.splice(index, 1);
      if (!state.length) state.push({ id: generateId(), name: '', value: '', tag: TAG_NEW });
      setCurrentState(state);
      props.onChange(stripTags(state));
    }
  }

  const rows = currentState.map(item => {
    const secrets = props.maskedItemNames || [];

    return (
      <NameValueListRow
        key={item.id}
        dataAid={`${props.dataAid}-row-${item.name}`}
        item={item}
        masked={secrets.includes(item.name) && item.tag !== TAG_NEW}
        resetError={resetError}
        errors={errors}
        onChange={handleRowChange}
        onDelete={handleRowDelete}
      />
    );
  });

  const disableAddButton = props.maxItemCount && currentState.length >= props.maxItemCount;
  const addNewRow = () => {
    if (disableAddButton) return;

    const state = [...currentState, { id: generateId(), name: '', value: '', tag: TAG_NEW }];
    setCurrentState(state);
    props.onChange(stripTags(state));
  };

  return (
    <LayoutWrapper>
      <FullWidthWrapper>
        <StyledTable zebra data-aid={props.dataAid}>
          <THead>
            <StyledHeaderTR>
              <StyledTH>{props.nameColumnLabel}</StyledTH>
              <StyledTH>{props.valueColumnLabel}</StyledTH>
              <StyledTH />
            </StyledHeaderTR>
          </THead>
          <TBody>{rows}</TBody>
        </StyledTable>
        <ActionRow>
          <Button isSmall={true} onClick={addNewRow} disabled={disableAddButton} data-aid="add-header-button">
            {props.addButtonLabel}
          </Button>
        </ActionRow>
      </FullWidthWrapper>
    </LayoutWrapper>
  );
};

const LayoutWrapper = styled.div`
  display: flex;
  flex-grow: 1;
  height: 100%;
  overflow: auto;
`;

const FullWidthWrapper = styled.div`
  width: 100%;
  flex-shrink: 1;
  min-height: 0;
  overflow: auto;
`;

const StyledHeaderTR = styled(TR)`
  padding: 0;
`;

const StyledTable = styled(Table)`
  border: 0;
  border-radius: 0;

  ${TH} {
    padding: 0 16px;
  }
`;

const ActionRow = styled.div`
  padding: 12px 6px;
`;

export default NameValueListEditor;
